// React and React-related imports
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Redux imports
import { selectHasPrivilegedRole } from '../../../../redux/slices/currentUserSlice';
import { selectAllShifts } from '../../../../redux/shifts/shiftsSlice';
import { selectShiftsByAssignment } from '../../../../redux/shifts/shiftsSelectors';
import { publishShiftsThunk, unpublishShiftsThunk } from '../../../../redux/shifts/shiftsThunks';
import { selectUpdatedPublishStatus, selectUpdatedUnpublishStatus } from '../../../../redux/shifts/shiftsSlice';

// Constants
import { LOADING_STATES } from '../../../../utils/constants/loadingStates';

// Component imports
import AutoStaffButton from '../../../AutoStaffButton/AutoStaffButton';
import { ShiftWeekNavigation } from '../../../../components';
import CustomTextButton from '../../../CustomTextButton/CustomTextButton';
import PublishConfirmationModal from '../PublishConfirmationModal/PublishConfirmationModal';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';

// Style and icon imports
import { StyledScheduleToolbar, StyledScheduleToolbarContainer, ButtonContainer } from './ScheduleToolbar.styles';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';

// Utilities and helpers
import { showSuccessToast, showErrorToast } from '../../../../utils/toastUtils';

const ScheduleToolbar = ({ onDateChange, onAddShift }) => {
  const dispatch = useDispatch();

  // Selectors
  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);
  const isPublishShiftLoading = useSelector(selectUpdatedPublishStatus) === LOADING_STATES.LOADING;
  const isUnpublishShiftLoading = useSelector(selectUpdatedUnpublishStatus) === LOADING_STATES.LOADING;
  const shifts = useSelector(selectAllShifts);
  const shiftsByAssignment = useSelector(selectShiftsByAssignment);

  // State
  const [isSchedulePublished, setIsSchedulePublished] = useState(false);
  const [openPublishConfirmationModal, setOpenPublishConfirmationModal] = useState(false);
  const [openUnpublishConfirmationModal, setOpenUnpublishConfirmationModal] = useState(false);

  // Derived values
  const shiftIds = shifts.map((shift) => shift.id);
  const hasUnassignedShifts = shiftsByAssignment.unassigned.length > 0;

  // Schedule publishing and unpublishing functions
  const handlePublishSchedule = async () => {
    try {
      const result = await dispatch(publishShiftsThunk(shiftIds)).unwrap();
      if (result) {
        setIsSchedulePublished(true);
        showSuccessToast('Shifts published successfully');
      }
    } catch (error) {
      console.error('Failed to publish shifts:', error);
      showErrorToast('Failed to publish shifts');
    }
  };

  const handlePublishChangesClick = async () => {
    setOpenPublishConfirmationModal(true);
  };

  const handleUnpublishChangesClick = async () => {
    setOpenUnpublishConfirmationModal(true);
  };

  const handleUnpublishSchedule = async () => {
    try {
      const result = await dispatch(unpublishShiftsThunk(shiftIds)).unwrap();
      if (result) {
        setIsSchedulePublished(false);
        showSuccessToast('Shifts unpublished successfully');
      }
    } catch (error) {
      console.error('Failed to unpublish shifts:', error);
      showErrorToast('Failed to unpublish shifts');
    }
  };

  // Monitor shifts to keep isSchedulePublished state up to date, which determines which button to show
  // Additionally, look for unpublished shifts in a published schedule and auto-publish them
  useEffect(() => {
    // Add debounce to prevent rapid-fire calls
    const timeoutId = setTimeout(async () => {
      const handleAutoPublish = async () => {
        if (isPublishShiftLoading || isUnpublishShiftLoading) {
          return;
        }

        const publishedShifts = shifts.filter((shift) => shift.isPublished);

        // If a schedule has any published shifts, consider it to be a published schedule
        const localIsSchedulePublished = publishedShifts.length > 0;
        setIsSchedulePublished(localIsSchedulePublished);

        // If the schedule is published, update any unpublished shifts
        // This ensures that adding a new shift to a published schedule will automatically publish it
        // We use the local variable because the state variable is updated asynchronously and won't be available in time
        if (localIsSchedulePublished) {
          const unpublishedShifts = shifts.filter((shift) => !shift.isPublished);

          if (unpublishedShifts.length > 0) {
            try {
              const shiftsToUpdate = unpublishedShifts.map((shift) => shift.id);
              // Use a single batch operation instead of individual updateShift calls to prevent duplicates
              await dispatch(publishShiftsThunk(shiftsToUpdate)).unwrap();
            } catch (error) {
              console.error('Failed to auto-publish shifts:', error);
              // Don't show error toast for auto-publish - it's not user-initiated
            }
          }
        }
      };

      handleAutoPublish();
    }, 300); // 300ms debounce

    // Cleanup timeout on unmount or dependency change
    return () => clearTimeout(timeoutId);
  }, [shifts, isPublishShiftLoading, isUnpublishShiftLoading, dispatch]);

  return (
    <StyledScheduleToolbar>
      <StyledScheduleToolbarContainer>
        <ShiftWeekNavigation onDateChange={onDateChange} />
      </StyledScheduleToolbarContainer>
      <ButtonContainer>
        {hasPrivilegedRole && (
          <>
            {isSchedulePublished ? (
              <CustomTextButton
                text="Unpublish Schedule"
                hasInfoIcon={true}
                infoTooltipText="Unpublishing this schedule will allow you to modify shifts without sending notifications to staff."
                onClick={handleUnpublishChangesClick}
                isLoading={isUnpublishShiftLoading}
                variant="secondaryDark"
              />
            ) : (
              <CustomTextButton
                text="Publish Schedule"
                hasInfoIcon={true}
                infoTooltipText="Publishing this schedule will notify all staff of changes that affect their shifts."
                onClick={handlePublishChangesClick}
                isLoading={isPublishShiftLoading}
              />
            )}
          </>
        )}
        <AutoStaffButton periodHasShifts={shifts.length > 0} />
        <CustomTextButton text="Shift" icon={AddBoxOutlinedIcon} onClick={onAddShift} variant="secondaryDark" />
      </ButtonContainer>
      <PublishConfirmationModal
        open={openPublishConfirmationModal}
        onPublish={handlePublishSchedule}
        onClose={() => setOpenPublishConfirmationModal(false)}
        hasUnassignedShifts={hasUnassignedShifts}
      />
      <ConfirmationModal
        open={openUnpublishConfirmationModal}
        onConfirm={handleUnpublishSchedule}
        onClose={() => setOpenUnpublishConfirmationModal(false)}
        title="Unpublish this schedule?"
        confirmButtonText="Unpublish"
      />
    </StyledScheduleToolbar>
  );
};

export default ScheduleToolbar;
